/**
 * CSV/MT940 Import – eigenständige Tab-Seite (Buchhaltung)
 */
import { useState, useRef, useEffect, useMemo } from 'react';
import { FileSpreadsheet, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useTabs } from '@/contexts/TabContext';
import { emitDataRefresh } from '@/lib/tabEvents';
import { toast } from 'sonner';

const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export default function CsvImportPage() {
  const { activeTab, closeTab } = useTabs();
  const params = activeTab?.params || {};
  const konten = useMemo(() => params.konten || [], [params.konten]);

  const [uploading, setUploading] = useState(false);
  const [kontoId, setKontoId] = useState(konten[0]?.id || '');
  const [format, setFormat] = useState('standard');
  const fileRef = useRef(null);

  useEffect(() => { setKontoId(konten[0]?.id || ''); }, [konten]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleImport = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error('Bitte Datei auswählen'); return; }
    if (!kontoId) { toast.error('Bitte Konto auswählen'); return; }
    const fileName = file.name.toLowerCase();
    const isMT940 = fileName.endsWith('.mt940') || fileName.endsWith('.sta') || fileName.includes('mt940');
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('konto_id', kontoId);
      formData.append('klient_id', params.klientId);
      if (!isMT940) formData.append('bank_format', format);
      const endpoint = isMT940 ? 'import/mt940' : 'import/csv';
      const res = await fetch(`${BACKEND_URL}/api/buchhaltung/${endpoint}`, {
        method: 'POST', body: formData
      }).then(r => r.json());
      if (res.success) {
        toast.success(`${res.importiert} Transaktionen importiert`);
        emitDataRefresh('buchhaltung');
        closeTab(activeTab.id);
      } else { toast.error(res.detail || 'Fehler beim Import'); }
    } catch (e) { console.error(e); toast.error('Import-Fehler'); }
    finally { setUploading(false); }
  };

  return (
    <div className="max-w-lg mx-auto" data-testid="csv-import-page">
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileSpreadsheet className="w-5 h-5 text-indigo-600" />CSV/MT940 Import
            </CardTitle>
            <Button variant="ghost" size="icon" onClick={() => closeTab(activeTab.id)}><X className="w-4 h-4" /></Button>
          </div>
          <CardDescription>Importieren Sie Transaktionen aus einer CSV- oder MT940-Datei Ihrer Bank</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Konto</Label>
            <Select value={kontoId} onValueChange={setKontoId}>
              <SelectTrigger><SelectValue placeholder="Konto wählen..." /></SelectTrigger>
              <SelectContent>
                {konten.map(k => <SelectItem key={k.id} value={k.id}>{k.bezeichnung} — {k.bank}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Bank-Format (für CSV)</Label>
            <Select value={format} onValueChange={setFormat}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="standard">Standard (automatisch)</SelectItem>
                <SelectItem value="sparkasse">Sparkasse</SelectItem>
                <SelectItem value="volksbank">Volksbank/Raiffeisen</SelectItem>
                <SelectItem value="dkb">DKB</SelectItem>
                <SelectItem value="ing">ING</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Datei (CSV, MT940)</Label>
            <input ref={fileRef} type="file" accept=".csv,.txt,.mt940,.sta"
              className="mt-1 block w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
            <p className="text-xs text-slate-400 mt-1">MT940-Dateien werden automatisch erkannt</p>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={() => closeTab(activeTab.id)}>Abbrechen</Button>
            <Button onClick={handleImport} disabled={uploading} data-testid="import-csv">
              {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Importiere...</> : <><FileSpreadsheet className="w-4 h-4 mr-2" />Importieren</>}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
